import { onlineManager } from "@tanstack/vue-query";
import { getDialogStack, tryGetDialogStack } from "@verbleif/lib/src/components/Dialog/dialogStack";
import { openConfirmLayer } from "@verbleif/lib/src/composables/Dialog/confirm";
import { queryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { useLogout } from "@web/features/Auth/Logout/useLogout";
import { reactive } from "vue";
import AccountsDialog from "./AccountsDialog.vue";
import LogoutChoiceDialogBody from "./LogoutChoiceDialogBody.vue";
import SessionExpiredDialogBody from "./SessionExpiredDialogBody.vue";

export type LogoutChoice = "one" | "all";

const ACCOUNTS_DIALOG_ID = "accounts-dialog";
const SESSION_EXPIRED_DIALOG_ID = "session-expired-dialog";
const DIALOG_WIDTH = 440;

/** Dialogs of the multi-account flow: the logout choice and the "Je accounts" list. */
export function useAccountDialogs() {
  const accountsStore = useAccountsStore();
  const { logout } = useLogout();

  function openAccountsDialog() {
    getDialogStack().push({
      id: ACCOUNTS_DIALOG_ID,
      component: AccountsDialog,
      size: "medium",
      headless: true,
      paddingless: true,
    });
  }

  /** Signs out directly with one account, asks what to sign out of with several. */
  async function requestLogout(): Promise<void> {
    const active = accountsStore.activeAccount.value;
    if (accountsStore.accounts.value.length < 2 || !active) {
      await logout();
      return;
    }

    const state = reactive<{ choice: LogoutChoice | null }>({ choice: null });
    const confirmed = await openConfirmLayer({
      component: LogoutChoiceDialogBody,
      size: "small",
      width: DIALOG_WIDTH,
      paddingless: true,
      props: {
        name: active.name,
        otherName: accountsStore.nextAccount.value?.name ?? null,
        count: accountsStore.accounts.value.length,
        choice: state.choice,
        confirmLoading: false,
        setChoice: (choice: LogoutChoice) => {
          state.choice = choice;
        },
      },
      sync: () => ({ choice: state.choice }),
    });
    if (!confirmed || state.choice === null) {
      return;
    }
    await logout({ allAccounts: state.choice === "all" });
  }

  return { openAccountsDialog, requestLogout };
}

// Queries and their refetches keep calling the API with the dead token, and each 401 starts another refresh.
// Pausing is done through the online manager, the documented way to hold queries; mutations ignore it.
function pauseQueries(): () => void {
  const wasOnline = onlineManager.isOnline();
  void queryClient.cancelQueries();
  onlineManager.setOnline(false);
  return () => onlineManager.setOnline(wasOnline);
}

/**
 * Asks what to do after the session of the account this tab works in was rejected. Returns false when there is no
 * other account to continue as or no dialog stack, so the caller keeps the single-account behaviour (sign out).
 * Opening it twice is a no-op: several failing requests report the same expired session.
 */
export function openSessionExpiredDialog(): boolean {
  const stack = tryGetDialogStack();
  const accountsStore = useAccountsStore();
  const active = accountsStore.activeAccount.value;
  const others = accountsStore.otherAccounts.value.filter(account => !account.expired);
  if (!stack || !active || others.length === 0) {
    return false;
  }
  if (stack.getById(SESSION_EXPIRED_DIALOG_ID)) {
    return true;
  }

  const { logout, switchAccount, reloginAccount } = useLogout();
  const resumeQueries = pauseQueries();
  const props = reactive({ name: active.name, others, busy: false });
  const instanceId = stack.push({
    id: SESSION_EXPIRED_DIALOG_ID,
    component: SessionExpiredDialogBody,
    size: "small",
    width: DIALOG_WIDTH,
    alert: true,
    mask: true,
    headless: true,
    paddingless: true,
    // The tab has no valid session behind this dialog, so it can only be left through one of its actions.
    dismissible: false,
    props,
    // Any other way the dialog goes away (pop, replace, closeAll) must not leave the queries held.
    onClose: resumeQueries,
    listeners: {
      continueAs: (index: number) => {
        props.busy = true;
        resumeQueries();
        switchAccount(index);
      },
      relogin: () => {
        props.busy = true;
        resumeQueries();
        reloginAccount(active.index);
      },
      logout: () => {
        props.busy = true;
        resumeQueries();
        // The session is dead on the auth server too, and the person asked to leave: not to continue as another account.
        void stack.pop(instanceId).then(() => logout({ logoutAuthServer: false, continueAsOtherAccount: false }));
      },
    },
  });
  return true;
}
